import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  screenReviewApi,
  type CustomerValueReviewSnapshot,
  type DashboardReviewSnapshot,
  type PriorityItem,
  type RedesignItem,
  type ReviewDashboardSnapshot,
  type ScreenEntry,
  type ScreenScore,
  type WorkflowReviewSnapshot,
} from '../../api/screen-review-xdb002-runtime';

type Tab = 'overview' | 'screens' | 'scores' | 'workflow' | 'priority' | 'redesign' | 'dashboard_review' | 'customer_value';

export function ScreenReviewPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<ReviewDashboardSnapshot | null>(null);
  const [screens, setScreens] = useState<ScreenEntry[]>([]);
  const [scores, setScores] = useState<ScreenScore[]>([]);
  const [workflow, setWorkflow] = useState<WorkflowReviewSnapshot | null>(null);
  const [priority, setPriority] = useState<PriorityItem[]>([]);
  const [redesign, setRedesign] = useState<RedesignItem[]>([]);
  const [dashReview, setDashReview] = useState<DashboardReviewSnapshot | null>(null);
  const [cvdReview, setCvdReview] = useState<CustomerValueReviewSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = 'border-rose-200 bg-white dark:border-rose-900/40 dark:bg-zinc-900';
  const muted = 'text-slate-500 dark:text-zinc-400';
  const accent = 'text-rose-700 dark:text-rose-300';
  const shell = 'bg-rose-50/20 dark:bg-zinc-950';

  const refresh = useCallback(async () => {
    setError(null); setLoading(true);
    try {
      const [dash, scr, sc, wf, pri, red, dr, cv] = await Promise.all([
        screenReviewApi.dashboard(), screenReviewApi.screens(), screenReviewApi.screenScores(),
        screenReviewApi.workflow(), screenReviewApi.priorityMatrix(), screenReviewApi.redesignPlan(),
        screenReviewApi.dashboardReview(), screenReviewApi.customerValueReview(),
      ]);
      setDashboard(dash); setScreens(scr); setScores(sc); setWorkflow(wf);
      setPriority(pri); setRedesign(red); setDashReview(dr); setCvdReview(cv);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Screen Review');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' }, { id: 'screens', label: 'Screen Catalog' },
    { id: 'scores', label: 'Screen Scores' }, { id: 'workflow', label: 'Workflow Review' },
    { id: 'priority', label: 'Priority Matrix' }, { id: 'redesign', label: 'Redesign Plan' },
    { id: 'dashboard_review', label: 'Dashboard Review' }, { id: 'customer_value', label: 'CVD Review' },
  ];

  const priorityColor = (p: string) => {
    if (p === 'critical') return 'text-red-600 bg-red-50 dark:bg-red-900/30';
    if (p === 'high') return 'text-orange-600 bg-orange-50 dark:bg-orange-900/30';
    if (p === 'medium') return 'text-amber-600 bg-amber-50 dark:bg-amber-900/30';
    return 'text-slate-600 bg-slate-50 dark:bg-zinc-800';
  };

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>XDB-000002 · Screen-by-Screen Experience Review</p>
            <h1 className="text-xl font-semibold">UX Audit Command Center</h1>
            {dashboard && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Review Score <strong className={accent}>{dashboard.overall_review_score}</strong></span>
                <span>Screens <strong className={accent}>{dashboard.screens_audited}/{dashboard.screens_total}</strong></span>
                <span>Critical <strong className="text-red-600">{dashboard.issues_critical}</strong></span>
                <span>Friction Points <strong className={accent}>{dashboard.workflow_friction_points}</strong></span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/experience-design" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Experience Design</Link>
            <Link to="/platform/screen-review-xdb002" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className={`text-sm ${muted}`}>Loading screen review data…</p>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? 'bg-rose-600 text-white' : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="grid gap-3 sm:grid-cols-4">
          {[
            { label: 'Overall Score', value: dashboard.overall_review_score },
            { label: 'Usability Avg', value: dashboard.avg_usability_score },
            { label: 'Accessibility Avg', value: dashboard.avg_accessibility_score },
            { label: 'Redesign Items', value: dashboard.redesign_items },
            { label: 'Critical Issues', value: dashboard.issues_critical },
            { label: 'High Issues', value: dashboard.issues_high },
            { label: 'Friction Points', value: dashboard.workflow_friction_points },
            { label: 'Trend', value: dashboard.improvement_trend },
          ].map((m) => (
            <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
              <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
              <p className="text-2xl font-semibold capitalize">{m.value}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'screens' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {screens.map((s) => (
            <div key={s.key} className={`rounded-lg border p-3 ${card}`}>
              <p className="font-medium">{s.name}</p>
              <p className={`text-xs ${muted}`}>{s.category} · {s.status}</p>
              <p className={`mt-1 text-sm ${muted}`}>{s.purpose}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'scores' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="w-full text-sm">
            <thead><tr className={`border-b ${muted}`}>
              <th className="p-3 text-left">Screen</th><th className="p-3 text-left">Grade</th>
              <th className="p-3 text-left">Usability</th><th className="p-3 text-left">Business Value</th>
              <th className="p-3 text-left">Clarity</th><th className="p-3 text-left">Trust</th>
            </tr></thead>
            <tbody>
              {scores.map((s) => (
                <tr key={s.screen_key} className="border-b border-rose-100 dark:border-rose-900/30">
                  <td className="p-3 font-medium">{s.screen_name}</td>
                  <td className="p-3"><span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${accent}`}>{s.overall_grade}</span></td>
                  <td className="p-3">{s.usability}</td><td className="p-3">{s.business_value}</td>
                  <td className="p-3">{s.clarity}</td><td className="p-3">{s.trust}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'workflow' && workflow && (
        <div className="space-y-3">
          <div className={`rounded-xl border p-4 ${card}`}>
            <p className={`text-sm ${muted}`}>Total clicks: <strong>{workflow.total_clicks}</strong> · Friction points: <strong>{workflow.friction_points}</strong> · Improvement potential: <strong>{workflow.improvement_pct}%</strong></p>
          </div>
          {workflow.steps.map((step) => (
            <div key={step.step} className={`rounded-lg border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium">{step.step}</p>
                <span className={`text-xs ${step.friction_score > 0.5 ? 'text-red-600' : muted}`}>Friction {step.friction_score}</span>
              </div>
              {step.friction && <p className={`mt-1 text-sm text-red-600 dark:text-red-400`}>{step.friction}</p>}
              <p className={`mt-1 text-xs ${muted}`}>{step.clicks} clicks</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'priority' && (
        <div className="space-y-2">
          {priority.map((item) => (
            <div key={item.id} className={`flex items-start justify-between gap-4 rounded-lg border p-4 ${card}`}>
              <div>
                <p className="font-medium">{item.issue}</p>
                <p className={`text-xs ${muted}`}>{item.screen}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize ${priorityColor(item.priority)}`}>{item.priority}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'redesign' && (
        <div className="space-y-3">
          {redesign.map((item) => (
            <div key={item.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold">{item.problem}</p>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs capitalize ${priorityColor(item.priority)}`}>{item.priority}</span>
              </div>
              <p className={`mt-2 text-sm ${muted}`}>Evidence: {item.evidence}</p>
              <p className="mt-1 text-sm"><strong className={accent}>Fix:</strong> {item.suggested_improvement}</p>
              <p className={`mt-2 text-xs ${muted}`}>Impact: {item.expected_impact} · Effort: {item.effort} · Screen: {item.screen}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'dashboard_review' && dashReview && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { label: 'What changed in 5 sec', ok: dashReview.what_changed_in_5sec },
              { label: 'What matters in 5 sec', ok: dashReview.what_matters_in_5sec },
              { label: 'What to do in 5 sec', ok: dashReview.what_to_do_in_5sec },
              { label: 'Why visible', ok: dashReview.why_visible },
              { label: 'Expected value visible', ok: dashReview.expected_value_visible },
            ].map((m) => (
              <div key={m.label} className="flex items-center justify-between">
                <span className={`text-sm ${muted}`}>{m.label}</span>
                <span className={m.ok ? 'text-rose-600' : 'text-red-600'}>{m.ok ? '✓ Pass' : '✗ Fail'}</span>
              </div>
            ))}
          </div>
          <p className={`mt-4 text-sm ${muted}`}>Pass rate: <strong className={accent}>{dashReview.pass_rate_pct}%</strong></p>
        </div>
      )}

      {tab === 'customer_value' && cvdReview && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { label: 'ROI in 30 seconds', ok: cvdReview.roi_in_30_seconds },
              { label: 'Executive clarity', ok: cvdReview.executive_clarity },
              { label: 'Presentation ready', ok: cvdReview.presentation_ready },
              { label: 'Simplification needed', ok: !cvdReview.simplification_needed, invert: true },
            ].map((m) => (
              <div key={m.label} className="flex items-center justify-between">
                <span className={`text-sm ${muted}`}>{m.label}</span>
                <span className={m.ok ? 'text-rose-600' : 'text-red-600'}>{m.ok ? '✓ Pass' : '✗ Fail'}</span>
              </div>
            ))}
          </div>
          <p className={`mt-4 text-sm ${muted}`}>Trust score: <strong className={accent}>{cvdReview.trust_score}</strong></p>
        </div>
      )}
    </div>
  );
}
